import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { NotificationDto } from '../dtos/notification.dto';

/**
 * WebSocket Service
 * Handles real-time WebSocket connections with automatic reconnection
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private authService = inject(AuthService);

  private socket: WebSocket | null = null;
  private notificationSubject = new Subject<NotificationDto>();
  private connectionStateSubject = new BehaviorSubject<boolean>(false);

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000; // 5 seconds
  private reconnectTimeout: any;
  private isIntentionalClose = false;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Get connection state as observable
   */
  public get connectionState$(): Observable<boolean> {
    return this.connectionStateSubject.asObservable();
  }

  /**
   * Connect to the WebSocket server with JWT authentication
   * @returns Observable stream of notifications
   */
  public connect(): Observable<NotificationDto> {
    // Check if already connected
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return this.notificationSubject.asObservable();
    }

    // Get JWT token for authentication
    const token = this.authService.getToken();
    if (!token) {
      console.error('No JWT token available for WebSocket connection');
      return this.notificationSubject.asObservable();
    }

    // Build WebSocket URL
    const wsUrl = this.buildWebSocketUrl(token);

    try {
      // Browser WebSocket does not support custom headers. Token is already in the URL.
      this.socket = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.handleReconnection();
    }

    return this.notificationSubject.asObservable();
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    this.isIntentionalClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close(1000, 'Client disconnect'); // 1000 = Normal Closure
      this.socket = null;
      this.connectionStateSubject.next(false);
    }
  }

  /**
   * Check if WebSocket is connected
   */
  public isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Build WebSocket URL with the JWT token
   */
  private buildWebSocketUrl(token: string): string {
    const baseUrl = environment.wsUrl;

    // Remove /api suffix if present (WebSocket endpoint is at root level)
    //const wsBaseUrl = baseUrl.replace(/\/api$/, '');

    // Build full WebSocket URL with JWT token
    // Format: ws(s)://hostname/ws/notifications/request?token=JWT_TOKEN
    return `${baseUrl}/ws/notifications/request?token=${token}`;
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection opened successfully
    this.socket.onopen = () => {
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this.isIntentionalClose = false;
      this.connectionStateSubject.next(true);
    };

    // Message received from server
    this.socket.onmessage = (event) => {
      try {
        const notification: NotificationDto = JSON.parse(event.data);
        // Emit notification to subscribers
        this.notificationSubject.next(notification);
      } catch (error) {
        console.error('Error parsing WebSocket notification:', error);
      }
    };

    // WebSocket error occurred
    this.socket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.connectionStateSubject.next(false);
    };

    // Connection closed
    this.socket.onclose = () => {
      this.connectionStateSubject.next(false);

      // Handle reconnection if not intentional close
      if (!this.isIntentionalClose) {
        this.handleReconnection();
      }
    };
  }

  /**
   * Handle automatic reconnection with exponential backoff
   */
  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`⚠️ Max reconnection attempts (${this.maxReconnectAttempts}) reached. Please refresh the page.`);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * this.reconnectAttempts; // Exponential backoff

    this.reconnectTimeout = setTimeout(() => {
      // Check if user is still authenticated before reconnecting
      if (this.authService.isAuthenticated()) {
        this.connect();
      }
    }, delay);
  }

  /**
   * Send a message through WebSocket (if needed for future features)
   * Note: Currently, the backend only sends notifications to clients
   */
  public send(message: any): void {
    if (this.isConnected() && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    } else {
      console.warn('Cannot send message: WebSocket not connected');
    }
  }
}
